import { MaterialIcons } from "@expo/vector-icons";
import { Formik } from "formik";
import _ from "lodash";
import { Button, useTheme } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { Calendar, DateData } from "react-native-calendars";
import type { MarkingProps } from "react-native-calendars/src/calendar/day/marking";
import * as Yup from "yup";

import type { MarkedDatesType } from "../components/GroceryListDatePicker";
import { Scale } from "../constants";
import { FontFamily } from "../constants/fonts";
import backendApi from "../services/backendApi";
import type { CalendarDay, User } from "../services/backendTypes";

const { usePlannerCopyMealsFromDateCreateMutation } = backendApi;

type Props = {
  user: User | null;
  onClose: () => void;
  currentCalendarDay: CalendarDay | undefined;
};

// eslint-disable-next-line import/prefer-default-export
export const CopyDayModal = ({ user, onClose, currentCalendarDay }: Props): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [copyFromDate, { isLoading: isLoadingCopyFromDate }] = usePlannerCopyMealsFromDateCreateMutation();

  interface CopyDayToValues {
    toCalendarDays: string[];
  }

  const onSubmitWrapper = async ({ toCalendarDays }: CopyDayToValues): Promise<void> => {
    if (!user) {
      throw new Error("User is not set");
    }
    if (!currentCalendarDay) {
      throw new Error("Current calendar day is not set");
    }
    if (!currentCalendarDay.id) {
      throw new Error("Current calendar day id is not set");
    }

    // Store the id in a variable that will be captured in the closure
    const fromCalendarDayId = currentCalendarDay.id;

    const copyDayPromises = _.map(toCalendarDays, (toCalendarDay: string) =>
      copyFromDate({
        copyFromDateRequest: {
          user: user.id,
          from_calendar_day_id: fromCalendarDayId,
          to_date: toCalendarDay,
        },
      })
    );

    await Promise.all(copyDayPromises);
    onClose();
  };

  const copyDayToSchema = Yup.object().shape({
    toCalendarDays: Yup.array(Yup.string()).required(t("general.required")),
  });

  const markedDateBaseProperties: MarkingProps = {
    color: theme.colors.primary["600"],
    textColor: "white",
  };

  const defaultSelectedDates: string[] = [];

  const getMarkedDates = (dates: string[]): MarkedDatesType => {
    const markedDates: MarkedDatesType = {};
    dates.forEach((dateString) => {
      markedDates[dateString] = markedDateBaseProperties;
    });
    return markedDates;
  };

  return (
    <>
      <Formik
        initialValues={{
          toCalendarDays: defaultSelectedDates,
        }}
        validationSchema={copyDayToSchema}
        onSubmit={onSubmitWrapper}
      >
        {({ isSubmitting, handleSubmit, values, isValid, setFieldValue }) => (
          <>
            <Calendar
              firstDay={1}
              markedDates={getMarkedDates([...values.toCalendarDays])}
              markingType="period"
              onDayPress={(toCalendarDay: DateData) => {
                const toCalendarDays = new Set(values.toCalendarDays);
                if (toCalendarDays.has(toCalendarDay.dateString)) {
                  toCalendarDays.delete(toCalendarDay.dateString);
                } else {
                  toCalendarDays.add(toCalendarDay.dateString);
                }

                setFieldValue("toCalendarDays", [...toCalendarDays]);
              }}
              renderArrow={(direction) => (
                <MaterialIcons
                  name={direction === "left" ? "chevron-left" : "chevron-right"}
                  size={24}
                  color={theme.colors.primary["600"]}
                />
              )}
              theme={{
                textDayFontFamily: FontFamily.medium,
                textMonthFontFamily: FontFamily.bold,
                textDayHeaderFontFamily: FontFamily.medium,
                textDayFontSize: Scale(14),
                textMonthFontSize: Scale(16),
                textDayHeaderFontSize: Scale(16),
                arrowColor: theme.colors.primary["600"],
              }}
            />
            <Button
              mt={3}
              isLoading={isLoadingCopyFromDate || isSubmitting}
              isDisabled={!isValid || values.toCalendarDays.length === 0}
              onPress={() => handleSubmit()}
              testID="copyDaySubmitButton"
              bg="primary.50"
              _pressed={{ bg: "primary.50" }}
              _text={{
                color: "white",
                fontWeight: "600",
              }}
            >
              {t("planner.copy_meals_to_modal.copy_to")}
            </Button>
          </>
        )}
      </Formik>
    </>
  );
};
